import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { StoreService } from '@ddt-web/store-service';
import { MeQueryParamKey, UrlQueryParamService, UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as isEmpty from 'lodash/isEmpty';
import * as isEqual from 'lodash/isEqual';
import { first } from 'rxjs/operators';

import { ModelCategoriesList } from '../../models/models-filter-customization.interface';

@UntilDestroy()
@Component({
	selector: 'ac-pdf-report-models-filter',
	templateUrl: './pdf-report-models-filter.component.html',
	styleUrls: ['./pdf-report-models-filter.component.scss']
})
export class PdfReportModelsFilterComponent {
	public showCustomization: boolean = false;
	public isCustomizationAvailable: boolean = false;
	public isToggleAll: boolean = true;
	public categories: ModelCategoriesList[] = [];
	public modelsList: string[] = [];

	@Output() public onSelectionChange: EventEmitter<string[]> = new EventEmitter<string[]>();

	public constructor(
		private storeService: StoreService,
		private urlService: UrlService,
		private router: Router,
		private urlQueryParamService: UrlQueryParamService,
		private activatedRoute: ActivatedRoute,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig
	) { }

	public ngOnInit(): void {
		const familyId = this.urlService.getModelFamilyId();
		this.storeService
			.selectById(this.storeConfig.families, familyId)
			.pipe(first(families => !families?.isLoading))
			.subscribe(families => {
				this.categories = this.generateFamilyIdModelsList(families?.[familyId]);
				this.updateCustomizationMode(this.categories);
			});

		// queryParams change detection to update selected models based on MeQueryParamKey.SELECTED_MODEL_IDS queryParam
		this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe(queryParams => {
			if (queryParams?.[MeQueryParamKey.SELECTED_MODEL_IDS]) {
				this.updateSelectedModelIdsFromUrl(queryParams[MeQueryParamKey.SELECTED_MODEL_IDS]);
			}
		});
	}

	public toggleShowCustomization(): void {
		this.showCustomization = !this.showCustomization;
	}

	public toggleAll(event: MouseEvent): void {
		// When click on All Sections checkbox
		this.isToggleAll = !this.isToggleAll;
		const configCopy = cloneDeep(this.categories);
		configCopy.forEach(section => {
			section.checked = this.isToggleAll; // set each section checkbox to isToggleAll
		});
		this.categories = configCopy;
		this.isToggleAll && this.modelsList ? this.addSelectedIdsToParams(this.modelsList) : this.addSelectedIdsToParams([]);
	}

	public onSectionCheck(sectionIndex: number): void {
		this.categories[sectionIndex].checked = !this.categories[sectionIndex].checked;
		const selectedIds = this.categories.filter(model => model.checked).map(model => model.id);
		this.isToggleAll = selectedIds.length === this.modelsList.length;
		this.addSelectedIdsToParams(selectedIds);
	}

	private generateFamilyIdModelsList(family): ModelCategoriesList[] {
		const familyModelsCategoryList: ModelCategoriesList[] = [];
		const idsList = [];

		family?.models.forEach(portfolio => {
			familyModelsCategoryList.push({
				id: portfolio.id,
				description: portfolio.shortName,
				checked: true
			});
			idsList.push(portfolio.id);
		});
		this.modelsList = idsList;
		this.onSelectionChange.emit(this.modelsList);
		return familyModelsCategoryList;
	}

	private addSelectedIdsToParams(selectedIds?: string[]): void {
		this.router.navigate([], {
			queryParams: this.urlQueryParamService.transformObjectToParams({
				[MeQueryParamKey.SELECTED_MODEL_IDS]: selectedIds
			}),
			queryParamsHandling: 'merge'
		});
		this.onSelectionChange.emit(selectedIds);
	}

	private updateSelectedModelIdsFromUrl(selectedIdsList: string): void {
		const selectedIds = selectedIdsList.split(',') ?? [];
		if (selectedIds?.[0] === 'list') {
			selectedIds.shift();
		}

		this.categories.forEach(category => {
			category.checked = false;
		});

		const validSelectedIds = selectedIds.filter(modelId => this.modelsList.includes(modelId));
		validSelectedIds.forEach(modelId => {
			const index = this.categories.findIndex(category => category.id === modelId);
			if (index !== -1) {
				this.categories[index].checked = true;
			}

			if (validSelectedIds.length < this.modelsList.length) {
				this.isToggleAll = false;
			}
			else if (isEqual(validSelectedIds, this.modelsList)) {
				this.isToggleAll = true;
			}
		});
	}

	private updateCustomizationMode(categories: ModelCategoriesList[]): void {
		this.isCustomizationAvailable = !isEmpty(categories);
	}
}
